import {
  CaregiverStatus,
  Instant,
  PossibleAnswer,
  PossibleAnswerId,
  TranslationMapping,
} from "../messages";
import {
  AgencyMemberId,
  CaregiverDocumentTypeId,
  HRQuestionGroupId,
  HRQuestionId,
  HRQuestionTemplateId,
  HRSectionId,
  HRSectionStageId,
  HRSubSectionId,
  PayRateSupplementalId,
  TrainingCenterBundleDueDateId,
} from "./ObjectIds";

export interface HRApplicationForm {
  sections: HRSection[];
  settings: HRApplicationFormSettings;
  orientationBundleDueDateId: TrainingCenterBundleDueDateId;
  caregiverSectionStageId: HRSectionStageId | null;
  caregiverSectionStageLabel: string | null;
  showMessageOnVideoThumbnail: boolean;
  isCriminalBackgroundCompliant: boolean;
  emailVerificationTemplateId: HRQuestionTemplateId | null;
  isInterviewCompliant: boolean | null;
}

export interface HRApplicationFormSettings {
  certificationSettings: CertificationSettingsForHRApp | null;
}

export interface CertificationSettingsForHRApp {
  locationPayRates: HRCertificationPayRate[];
}

export interface HRCertificationPayRate {
  locationGroup: string;
  counties: string[];
  rates: {
    hourlyRate: number;
    dailyRate: number;
    overtimeRate: number;
    visitRate: number;
    payRange: string | null;
    baseWage: number | null;
    additionalWage: number | null;
    supplementalWage: number | null;
    workShiftLine1Rate: number | null;
    workShiftLine1Label: string | null;
    workShiftLine2Rate: number | null;
    workShiftLine2Label: string | null;
  };
  supplementals: PayRateSupplemental[];
}

export interface PayRateSupplemental {
  id: PayRateSupplementalId;
  state: string;
  groupName: string;
  hourlyRate: number;
  type: string;
  agencyName: string;
  agencyDescription: string;
}

export type HRSectionTranslations = TranslationMapping<{
  title: string;
  subtitle: string | null;
}>;

export interface HRSection {
  data: HRSectionData;
  subSections: HRSubSection[];
}

export interface HRSectionData {
  id: HRSectionId;
  title: string;
  subtitle: string | null;
  isStop: boolean;
  HRSectionStageId: HRSectionStageId | null;
  HRSectionStageLabel: string | null;
  secondsToComplete: number | null;
  translations: HRSectionTranslations | null;
  locked: boolean;
}

export interface HRSubSection {
  data: HRSubSectionData;
  document: SubsectionDocumentData | null;
  questionGroups: HRQuestionGroup[];
}

interface SubsectionDocumentData {
  // documentType?: HRDocumentTypeId;
  isDocumentGenerated: boolean;
  doc: string | null;
  // secondsToComplete: number | null; <-- ???
}

export interface HRQuestionGroup {
  data: HRQuestionGroupData;
  questions: HRQuestion[];
}

export type HRQuestion =
  | HRQuestion.TextQuestion
  | HRQuestion.AddressQuestion
  | HRQuestion.EmailQuestion
  | HRQuestion.NumericQuestion
  | HRQuestion.DollarQuestion
  | HRQuestion.PhoneNumberQuestion
  | HRQuestion.SSNQuestion
  | HRQuestion.FullDateQuestion
  | HRQuestion.BirthDateQuestion
  | HRQuestion.MonthYearDateQuestion
  | HRQuestion.YearDateQuestion
  | HRQuestion.RadioQuestion
  | HRQuestion.DropDownQuestion
  | HRQuestion.CheckboxQuestion
  | HRQuestion.SignatureQuestion
  | HRQuestion.GeneralSignatureQuestion
  | HRQuestion.StartAndEndTimeQuestion
  | HRQuestion.StarQuestion
  | HRQuestion.Placeholder
  | HRQuestion.DocumentQuestion
  | HRQuestion.DocumentWithExpiryQuestion
  | HRQuestion.AmericanTestQuestion
  | HRQuestion.FreeTextQuestion
  | HRQuestion.TitelTextQuestion
  | HRQuestion.PlainTextQuestion
  | HRQuestion.DownloadableFileQuestion
  | HRQuestion.Video
  | HRQuestion.InterviewQuestion;

export namespace HRQuestion {
  export const questionTypes = {
    Text: "Text",
    Address: "Address",
    Email: "Email",
    Numeric: "Numeric",
    Dollar: "Dollar",
    PhoneNumber: "PhoneNumber",
    SSN: "SSN",
    FullDate: "FullDate",
    BirthDate: "BirthDate",
    MonthYearDate: "MonthYearDate",
    YearDate: "YearDate",
    Radio: "Radio",
    DropDown: "DropDown",
    Checkbox: "Checkbox",
    Signature: "Signature",
    GeneralSignature: "GeneralSignature",
    StartAndEndTime: "StartAndEndTime",
    Star: "Star",
    Placeholder: "Placeholder",
    Document: "Document",
    DocumentWithExpiry: "DocumentWithExpiry",
    AmericanTest: "AmericanTest",
    FreeText: "FreeText",
    TitleText: "TitleText",
    PlainText: "PlainText",
    DownloadableFile: "DownloadableFile",
    Video: "Video",
    Interview: "Interview",
  };
  export type QuestionType = keyof typeof questionTypes;
  export function isValidQuestionType(x: string): x is QuestionType {
    return x in questionTypes;
  }

  export type HRQuestionTranslations = TranslationMapping<string>;
  export type HRPossibleAnswerTranslations = TranslationMapping<PossibleAnswer[]>;

  export const HRComplianceDocumentStatus = [
    "Rejected",
    "Dismissed",
    "Approved",
    "Pending",
  ] as const;
  export type HRComplianceDocumentStatus =
    | {
        type: "Rejected";
        reason: string | null;
      }
    | {
        type: "Dismissed";
      }
    | {
        type: "Approved";
      }
    | {
        type: "Pending";
      };

  export const HREmailVerificationQuestionStatus = ["Verified", "Pending"] as const;
  export type HREmailVerificationQuestionStatus =
    | {
        type: "Verified";
      }
    | {
        type: "Pending";
      };

  export interface HRQuestionRejectionStatusRejected {
    status: "Rejected";
    rejectedAt: Instant;
    rejectedBy: AgencyMemberId;
    reason: string;
  }
  export interface HRQuestionRejectionStatusNotRejected {
    status: "Not Rejected";
  }

  interface QuestionData {
    id: HRQuestionId;

    /**
     * If question has no template, this will be the HRQuestionId in text format,
     * because of hr app client reasons.
     */
    textId: HRQuestionTemplateId;

    questionText: string;
    questionTextTranslations: HRQuestionTranslations | null;
    parentId: HRQuestionId | null;
    parentAnswer: string | null;
    isHidden: boolean;
    isMandatory: boolean;
    hideOnMobile?: boolean;
    readonly type: QuestionType;
    caregiverProfileParam?: string | null;
    linkedQuestionTemplate?: string | null;
    answerRejectionStatus: HRQuestionRejectionStatusRejected | HRQuestionRejectionStatusNotRejected;
    emailVerificationRejectionStatus: HREmailVerificationQuestionStatus | null;
    isComplianceFollowUp?: boolean | null;
    answeredAt?: Instant;
  }

  export interface TextQuestion extends QuestionData {
    readonly type: "Text";
    answer: string | null;
  }

  export interface AddressQuestion extends QuestionData {
    readonly type: "Address";
    answer: string | null;
  }

  export interface EmailQuestion extends QuestionData {
    readonly type: "Email";
    answer: string | null;
  }

  export interface NumericQuestion extends QuestionData {
    readonly type: "Numeric";
    answer: string | null;
  }

  export interface DollarQuestion extends QuestionData {
    readonly type: "Dollar";
    answer: string | null;
  }

  export interface PhoneNumberQuestion extends QuestionData {
    readonly type: "PhoneNumber";
    answer: string | null;
  }

  export interface SSNQuestion extends QuestionData {
    readonly type: "SSN";
    answer: string | null;
  }

  export interface FullDateQuestion extends QuestionData {
    readonly type: "FullDate";
    answer: string | null;
  }

  export interface BirthDateQuestion extends QuestionData {
    readonly type: "BirthDate";
    answer: string | null;
  }

  export interface InterviewQuestion extends QuestionData {
    readonly type: "Interview";
    possibleAnswers: PossibleAnswer[];
    possibleAnswersTranslations: HRPossibleAnswerTranslations | null;
    chosenAnswerId: PossibleAnswerId | null;
    answer: string | null;
  }

  export interface MonthYearDateQuestion extends QuestionData {
    readonly type: "MonthYearDate";
    answer: string | null;
  }

  export interface YearDateQuestion extends QuestionData {
    readonly type: "YearDate";
    answer: string | null;
  }

  export interface RadioQuestion extends QuestionData {
    readonly type: "Radio";
    possibleAnswers: PossibleAnswer[];
    possibleAnswersTranslations: HRPossibleAnswerTranslations | null;
    chosenAnswerId: PossibleAnswerId | null;
  }

  export interface DropDownQuestion extends QuestionData {
    readonly type: "DropDown";
    possibleAnswers: PossibleAnswer[];
    possibleAnswersTranslations: HRPossibleAnswerTranslations | null;
    chosenAnswerId: PossibleAnswerId | null;
  }

  export interface AmericanTestQuestion extends QuestionData {
    readonly type: "AmericanTest";

    possibleAnswers: PossibleAnswer[];
    possibleAnswersTranslations: HRPossibleAnswerTranslations | null;
    chosenAnswerId: PossibleAnswerId | null;
    correctAnswer: PossibleAnswerId;
  }

  export interface CheckboxQuestion extends QuestionData {
    readonly type: "Checkbox";
    possibleAnswers: PossibleAnswer[];
    possibleAnswersTranslations: HRPossibleAnswerTranslations | null;
    chosenAnswerIds: PossibleAnswerId[] | null;
  }

  export interface FreeTextQuestion extends QuestionData {
    readonly type: "FreeText";
    possibleAnswers: PossibleAnswer[];
    possibleAnswersTranslations: HRPossibleAnswerTranslations | null;
    chosenAnswerIds: PossibleAnswerId[] | null;
  }

  export interface StarQuestion extends QuestionData {
    readonly type: "Star";
    answer: number | null;
  }

  export interface SignatureQuestion extends QuestionData {
    readonly type: "Signature";
    /**
     * base64 data-url png of signature
     */
    answer: string | null;
  }

  export interface GeneralSignatureQuestion extends QuestionData {
    readonly type: "GeneralSignature";
    /**
     * base64 data-url png of signature
     */
    answer: GeneralSignatureAnswer | null;
  }

  export interface StartAndEndTimeQuestion extends QuestionData {
    readonly type: "StartAndEndTime";
    answer: null | { startTime: number; endTime: number };
  }

  export interface Placeholder extends QuestionData {
    readonly type: "Placeholder";
    isMandatory: false;
  }

  export interface Video extends QuestionData {
    readonly type: "Video";
    isMandatory: false;
  }

  export interface TitelTextQuestion extends QuestionData {
    readonly type: "TitleText";
    isMandatory: false;
  }

  export interface PlainTextQuestion extends QuestionData {
    readonly type: "PlainText";
    isMandatory: false;
  }

  export interface DocumentQuestion extends QuestionData {
    readonly type: "Document";
    /**
     * data uri
     */
    answer: string | null;
    documentTypeToUpload: CaregiverDocumentTypeId | null;
    documentTypeNameTranslations?: HRDocumentTitleTranslations;
    complianceRejectionData?: HRComplianceDocumentStatus | null;
    canUploadFromGallery: boolean;
  }

  export interface DocumentWithExpiryQuestion extends QuestionData {
    readonly type: "DocumentWithExpiry";
    answer: DocumentWithExpiryAnswer | null;
    documentTypeToUpload: CaregiverDocumentTypeId | null;
    documentTypeNameTranslations?: HRDocumentTitleTranslations;
    complianceRejectionData?: HRComplianceDocumentStatus | null;
  }

  export interface DownloadableFileQuestion extends QuestionData {
    readonly type: "DownloadableFile";
    downloaded: boolean;
    answer: string | null;
  }
}

export type HRDocumentTitleTranslations = TranslationMapping<string>;

export interface GeneralSignatureAnswer {
  signature: string | null;
  generalSignaturePerson: string | null;
}

export interface DocumentWithExpiryAnswer {
  document: string;
  expiryDate: string | null;
}

export interface HRAnswerResult {
  isSuccessful: boolean;
  reason?: string;
}

export type HRQuestionGroupTranslations = TranslationMapping<{
  title: string;
  subtitle: string | null;
}>;

export interface HRQuestionGroupData {
  id: HRQuestionGroupId;
  title: string;
  subtitle: string | null;
  translations: HRQuestionGroupTranslations | null;
}

export type HRSubSectionTranslations = TranslationMapping<{
  title: string;
  subtitle: string | null;
}>;

export interface HRSubSectionData {
  id: HRSubSectionId;
  title: string;
  subtitle: string | null;
  settings: HRSubSectionSettings | null;
  translations: HRSubSectionTranslations | null;
  shareUrl: string | null;
  isCompliant: boolean | null;
}

export interface HRSubSectionSettings {
  hideOnMobile?: boolean;
  testSettings: HRTestSettings | null;
  availabilityPlaceholder: HRAvailabilityPlaceholderSettings | null;
  hideOnApplication?: boolean;
}

export interface HRTestSettings {
  // TODO rename?
  testFailText: string;
  testSuccessText: string;
  testRetryText: string;
  maxWrongAnswers: number;
  testMaxTries: number;
  shouldShowWrongAnswers: boolean;
}

export interface HRAvailabilityPlaceholderSettings {
  showHoursQuestion: boolean;
  showDistanceQuestion: boolean;
}

export type OnboardingStageName =
  | "generalDetails"
  | "landingPage"
  | "onboarding1"
  | "onboarding2"
  | "interview";

export interface CaregiverApplicationBannerResponse {
  show: boolean;
  chatAgencyMemberId: AgencyMemberId | null;
  onboardingStage: OnboardingStageName | null;
  caregiverStatus: CaregiverStatus | null;
}
